/**
 * Reusable "Config Field" row.
 *
 * Displays a value with:
 *   - Manual editability (locked fields refuse edits)
 *   - AI Suggestion badge with confidence
 *   - "Reset to AI" affordance when ai_value ≠ value
 *   - Review status badge
 *   - Explanation tooltip
 *
 * Backed by the ProjectConfiguration ``slot`` shape returned by
 * ``GET /api/projects/{pid}/configuration``.
 */
import { useState, useEffect } from "react";
import {
  Sparkle, LockKey, ArrowClockwise, CheckCircle, Warning, PencilSimple,
  Info,
} from "@phosphor-icons/react";

const REVIEW_TONE = {
  "AI Suggested":              { tone: "blue" },
  "Draft":                      { tone: "slate" },
  "Manually Edited":            { tone: "amber" },
  "Validated":                  { tone: "sky" },
  "Technical Review Required":  { tone: "amber" },
  "Technically Approved":       { tone: "emerald" },
  "CP/QP Review Required":      { tone: "rose" },
  "CP/QP Approved":             { tone: "emerald" },
  "Rejected":                   { tone: "rose" },
  "Superseded":                 { tone: "slate" },
  "Locked for Publication":     { tone: "violet" },
};
const TONE_CLS = {
  blue:    "border-brand-blue/30 bg-brand-blue/5 text-brand-blue",
  slate:   "border-slate-200 bg-slate-50 text-slate-600",
  amber:   "border-amber-200 bg-amber-50 text-amber-800",
  sky:     "border-sky-200 bg-sky-50 text-sky-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rose:    "border-rose-200 bg-rose-50 text-rose-800",
  violet:  "border-violet-200 bg-violet-50 text-violet-800",
};

export default function ConfigFieldRow({
  section, field, label, unit, type = "text", step,
  options = [],
  slot = {}, onChange, onResetToAI, hint,
}) {
  const {
    value, ai_value, confidence, review_status = "Draft",
    locked, explanation, source,
  } = slot;

  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  const commit = () => {
    if (locked) return;
    let out = draft;
    if (out === "" || out === undefined) out = null;
    if (type === "number" && out !== null) out = Number(out);
    if (out !== value) onChange?.(out);
  };

  const showAI = ai_value != null && ai_value !== value;
  const tone   = REVIEW_TONE[review_status]?.tone || "slate";
  const cls    = TONE_CLS[tone] || TONE_CLS.slate;
  const testId = `cf-${section}-${field}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 md:gap-6 px-4 py-3 items-start"
             data-testid={testId}>
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 shrink-0 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
          {locked ? <LockKey size={12} className="text-violet-600" weight="fill" />
                    : source === "ai_suggested" ? <Sparkle size={12} className="text-brand-blue" weight="fill" />
                    : <PencilSimple size={12} className="text-slate-500" />}
        </div>
        <div>
          <div className="text-[12.5px] font-semibold text-slate-900">{label}</div>
          <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm border text-[9px] uppercase tracking-widest ${cls}`}
                     data-testid={`${testId}-status`}>
              {review_status}
            </span>
            {confidence != null && (
              <span className="text-[9.5px] font-mono" data-testid={`${testId}-confidence`}>
                conf {Math.round(confidence * 100)}%
              </span>
            )}
            {hint && <span className="text-[9.5px] italic">{hint}</span>}
          </div>
          {explanation && (
            <div className="text-[10px] text-slate-500 mt-1 flex items-start gap-1"
                    data-testid={`${testId}-explanation`}>
              <Info size={9} className="mt-0.5 shrink-0" />
              <span className="italic">{explanation}</span>
            </div>
          )}
        </div>
      </div>
      <div>
        {type === "select" ? (
          <select value={draft ?? ""} onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    disabled={locked}
                    data-testid={`${testId}-input`}
                    className={`w-full h-10 rounded-md border px-3 text-[13px] ${locked ? "bg-slate-50 border-slate-100 text-slate-500" : "border-slate-200 bg-white"}`}>
            <option value="">—</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <div className="flex items-center gap-2">
            <input type={type} step={step}
                     value={draft ?? ""}
                     onChange={(e) => setDraft(e.target.value)}
                     onBlur={commit}
                     onKeyDown={(e) => e.key === "Enter" && commit()}
                     disabled={locked}
                     data-testid={`${testId}-input`}
                     className={`flex-1 h-10 rounded-md border px-3 text-[13px] font-mono ${locked ? "bg-slate-50 border-slate-100 text-slate-500" : "border-slate-200 bg-white"}`} />
            {unit && <span className="text-[11px] text-slate-500 font-mono shrink-0">{unit}</span>}
          </div>
        )}
        {showAI && (
          <div className="mt-1.5 flex items-center gap-2 text-[11px]">
            <span className="text-slate-500">AI suggests</span>
            <span className="font-mono text-brand-blue" data-testid={`${testId}-ai-value`}>{String(ai_value)}</span>
            {!locked && onResetToAI && (
              <button type="button" onClick={() => onResetToAI(ai_value)}
                          data-testid={`${testId}-reset-ai`}
                          className="inline-flex items-center gap-0.5 text-brand-blue hover:underline text-[10.5px]">
                <ArrowClockwise size={9} /> Use AI
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
